import { useCallback, useEffect, useState } from "react";
import type { User } from "./useUser";

// Gate for the first-run welcome flow. The overlay shows when:
//
//   - localStorage["pmt-onboarded"] !== "true"     (not yet dismissed)
//   - products.length === 0                        (haven't started yet)
//   - AND EITHER of:
//       a. localStorage["pmt-signup-intent"] === "1"  (came in via /signup)
//       b. user.created_at < ONBOARDING_WINDOW_MS ago (fresh account, any path)
//
// 0024 added (a) as a positive intent signal and `Login` clears it on
// mount (F10) so a stale flag from an abandoned signup can't fire
// onboarding for a returning-user signin. (b) stays as a backstop for
// the cross-device case (same user opens app on a second device within
// 5 min — fresh enough to deserve onboarding even though the laptop's
// localStorage doesn't have the flag).
//
// The 5-minute account-age window is intentionally narrow: it's the
// belt-and-braces for cleared-localStorage. The localStorage flag is
// the real "completed" signal once onboarding wraps up (markComplete
// clears both pmt-onboarded and pmt-signup-intent).
//
// This hook deliberately doesn't fetch products itself — Dashboard already
// has that query and we'd just duplicate the round-trip. Caller passes
// productsCount in.

export const ONBOARDING_FLAG_KEY = "pmt-onboarded";
export const SIGNUP_INTENT_FLAG_KEY = "pmt-signup-intent";
export const ONBOARDING_WINDOW_MS = 5 * 60 * 1000;

export function useOnboardingState({
  user,
  productsCount,
  productsLoading,
}: {
  user: User | null | undefined;
  productsCount: number;
  productsLoading: boolean;
}) {
  // Flag is read once on mount and updated locally; we don't subscribe to
  // storage events because the only writer in v1 is this same hook (single
  // tab in practice for a brand-new user during the welcome flow).
  const [dismissed, setDismissed] = useState<boolean>(() => readFlag());

  // Re-read the flag if it was set in another tab between mount and now.
  // Cheap; runs on every render of the consumer but only writes state
  // when the value actually changed.
  useEffect(() => {
    const current = readFlag();
    if (current !== dismissed) setDismissed(current);
  }, [dismissed]);

  const markComplete = useCallback(() => {
    try {
      window.localStorage.setItem(ONBOARDING_FLAG_KEY, "true");
      // Clear the signup-intent flag too so it can't re-trigger
      // onboarding on a future signin (0024 F10).
      window.localStorage.removeItem(SIGNUP_INTENT_FLAG_KEY);
    } catch {
      // localStorage can throw in private mode / quota-exceeded; the
      // overlay still dismisses for the session via component state.
    }
    setDismissed(true);
  }, []);

  const accountAgeMs = user?.created_at
    ? Date.now() - new Date(user.created_at).getTime()
    : Number.POSITIVE_INFINITY;
  const isFreshAccount = accountAgeMs >= 0 && accountAgeMs < ONBOARDING_WINDOW_MS;

  // intentSignup is the positive signal: "this user came in via /signup."
  // Login clears it on mount (F10) so stale flags from abandoned signups
  // don't fire onboarding for returning-user signins.
  const intentSignup = readSignupIntent();

  // While products are still loading we don't render the overlay yet —
  // showing it for a beat and then snapping it away (because the user
  // actually had products) is worse than the brief blank moment.
  const shouldShow =
    !!user &&
    !dismissed &&
    (intentSignup || isFreshAccount) &&
    !productsLoading &&
    productsCount === 0;

  return {
    shouldShow,
    markComplete,
  };
}

function readFlag(): boolean {
  try {
    return window.localStorage.getItem(ONBOARDING_FLAG_KEY) === "true";
  } catch {
    return false;
  }
}

function readSignupIntent(): boolean {
  try {
    return window.localStorage.getItem(SIGNUP_INTENT_FLAG_KEY) === "1";
  } catch {
    return false;
  }
}
